import { WIDGET_TYPES } from 'common/Widgets/constants';
import { ComingSoonFeatureKey } from 'components/ComingSoonFeatures/constants';

export const OVERALL_VIEW_ID = -1;
export const OVERALL_VIEW_KEY = 'overall';
export const OVERALL_TAB_LABEL = 'Overall';
export const DEFAULT_LAGGING_COUNT = 0;

export const AICI_REGENERATION_FEATURE = {
  ACTION_PLANS: 'action_plans',
  SALES_TALK_TRACKS: 'sales_talk_tracks',
} as const;

export const AICI_REGENERATION_FEATURE_OPTIONS = [
  { label: 'Action Plans', value: AICI_REGENERATION_FEATURE.ACTION_PLANS, dateKey: 'action_plans_last_generated_at' },
  {
    label: 'Sales Talk Tracks',
    value: AICI_REGENERATION_FEATURE.SALES_TALK_TRACKS,
    dateKey: 'sales_talk_tracks_last_generated_at',
  },
] as const;

export const AICI_REGENERATE_MENU_KEY = 'regenerate';
export const AICI_LAST_GENERATED_DATE_FORMAT = 'MMM D, YYYY';

export const OVERALL_AI_CI_LABEL = 'Overall AI CI';

export const RECOMMENDATION_WEB_EVIDENCE_QUERY_KEY = 'recommendation-web-evidence';
export const WEBSITE_ACCESS_STATUS_LABELS: Record<string, string> = {
  access_denied: 'Access denied by website',
  rate_limited: 'Rate limited by website',
  browser_challenge: 'Browser verification required',
};
export const WEBSITE_EXPERIENCE_DATA_POINT_VALUE = 'website_experience';
export const RECOMMENDATION_WEB_EVIDENCE_MODAL_WIDTH = '80%';
export const RECOMMENDATION_WEB_EVIDENCE_CLAIMS_TAB_KEY = 'verified-claims';

export const RECOMMENDATION_VERIFICATION_FAILURE_REASONS = {
  NO_EVIDENCE: 'no_evidence',
  MISSING_COMMUNITY_EVIDENCE: 'missing_community_evidence',
  CLAIM_VALIDATION_FAILED: 'claim_validation_failed',
  REQUEST_TIMEOUT: 'verification_request_timed_out',
} as const;

export const RECOMMENDATION_VERIFICATION_FAILURE_LABELS: Record<string, string> = {
  [RECOMMENDATION_VERIFICATION_FAILURE_REASONS.REQUEST_TIMEOUT]:
    'The AI verification request timed out. Collected website evidence is still available.',
  [RECOMMENDATION_VERIFICATION_FAILURE_REASONS.NO_EVIDENCE]: 'No website evidence was collected for this insight.',
  [RECOMMENDATION_VERIFICATION_FAILURE_REASONS.MISSING_COMMUNITY_EVIDENCE]:
    'Website evidence is missing for some compared communities.',
  [RECOMMENDATION_VERIFICATION_FAILURE_REASONS.CLAIM_VALIDATION_FAILED]:
    'The insight claims could not be matched to the collected evidence.',
};
export const RECOMMENDATION_VERIFICATION_FAILURE_DEFAULT_LABEL = 'Insight verification failed.';

export const AICI_ANALYSIS_SCOPE = {
  COMMUNITY: 'community',
  COMPANY: 'company',
} as const;

export type AICIAnalysisScope = (typeof AICI_ANALYSIS_SCOPE)[keyof typeof AICI_ANALYSIS_SCOPE];

export const AICI_SWITCHER_VIEW = {
  OVERALL: 'overall',
  SPEED_TO_LEAD: 'speed-to-lead',
} as const;

export type AICISwitcherView = (typeof AICI_SWITCHER_VIEW)[keyof typeof AICI_SWITCHER_VIEW] | ComingSoonFeatureKey;

export const AI_CI_PERFORMANCE = {
  LEADING: 'leading',
  LAGGING: 'lagging',
  NEUTRAL: 'neutral',
} as const;

export type AICIPerformanceType = (typeof AI_CI_PERFORMANCE)[keyof typeof AI_CI_PERFORMANCE];

export const PERFORMANCE_OPTIONS = [
  { label: 'Leading', value: AI_CI_PERFORMANCE.LEADING },
  { label: 'Lagging', value: AI_CI_PERFORMANCE.LAGGING },
];

export const SCORE_SIGN = {
  POSITIVE: 'positive',
  NEGATIVE: 'negative',
} as const;

export type ScoreSignType = (typeof SCORE_SIGN)[keyof typeof SCORE_SIGN];

export const SALES_PLAYBOOK_STATUS = {
  LEAD_WITH: 'LEAD_WITH',
  POSITION_AGAINST: 'POSITION_AGAINST',
  HANDLE_OBJECTIONS: 'HANDLE_OBJECTIONS',
} as const;

export const SALES_PLAYBOOK_VARIANT_CLASS = {
  [SALES_PLAYBOOK_STATUS.LEAD_WITH]: 'lead-with',
  [SALES_PLAYBOOK_STATUS.POSITION_AGAINST]: 'position-against',
  [SALES_PLAYBOOK_STATUS.HANDLE_OBJECTIONS]: 'handle-objections',
} as const;

export const SALES_PLAYBOOK_BADGE_LABEL = {
  [SALES_PLAYBOOK_STATUS.LEAD_WITH]: 'Strength',
  [SALES_PLAYBOOK_STATUS.POSITION_AGAINST]: 'Competitive',
  [SALES_PLAYBOOK_STATUS.HANDLE_OBJECTIONS]: 'Gap',
} as const;

export const SALES_TALK_TRACKS_LABEL = 'Sales Talk Tracks';
export const SALES_TALK_TRACKS_ROUTE = '/sales-talk-tracks';
export const SALES_TALK_TRACK_CATEGORY_ID_PREFIX = 'sales-talk-track-category-';
export const SALES_PLAYBOOK_SECTION_ID = {
  LEAD_WITH: 'lead-with-section',
  POSITION_AGAINST: 'position-against-section',
  HANDLE_OBJECTIONS: 'handle-objections-section',
} as const;

export const CATEGORY_INSIGHT_QUERY_KEY_SCOPE = 'ai-ci-category-insights';
export const CATEGORY_DETAILS_TITLE = 'Category Details';
export const CATEGORY_INSIGHT_SECTION_TITLE = {
  STRENGTHS: 'Your Strengths',
  WEAKNESSES: 'Your Weaknesses',
  KEY_COMPETITIVE_DIFFERENCES: 'Key Competitive Differences',
  RECOMMENDED_ACTIONS: 'Recommended Actions',
} as const;

export const CATEGORY_INSIGHT_VARIANT = {
  STRENGTHS: 'strengths',
  DIFFERENCES: 'differences',
  WEAKNESSES: 'weaknesses',
  ACTIONS: 'actions',
} as const;

export const RECOMMENDATION_CATEGORIES = {
  rates: {
    label: 'Rates',
    value: 'rates',
    background: 'var(--light-green)',
    color: 'var(--positive-green)',
    gradientFrom: 'var(--mint-green)',
    gradientTo: 'var(--positive-green)',
  },
  online_reviews: {
    label: 'Online Reviews',
    value: 'online_reviews',
    background: 'var(--peach-pale)',
    color: 'var(--inactive-orange)',
    gradientFrom: 'var(--amber-light)',
    gradientTo: 'var(--inactive-orange)',
  },
  occupancy: {
    label: 'Occupancy',
    value: 'occupancies',
    background: 'var(--background-gray-soft)',
    color: 'var(--neutral-category-text)',
    gradientFrom: 'var(--line-gray)',
    gradientTo: 'var(--line-gray-strong)',
  },
  incentives: {
    label: 'Incentives',
    value: 'incentives',
    background: 'var(--aqua-pale)',
    color: 'var(--turquoise)',
    gradientFrom: 'var(--turquoise-light)',
    gradientTo: 'var(--turquoise)',
  },
  fees: {
    label: 'Fees',
    value: 'fees',
    background: 'var(--lavender-soft)',
    color: 'var(--competitor-purple)',
    gradientFrom: 'var(--lavender-purple)',
    gradientTo: 'var(--competitor-purple)',
  },
  amenities: {
    label: 'Amenities',
    value: 'amenities',
    background: 'var(--faded-pink)',
    color: 'var(--hot-pink)',
    gradientFrom: 'var(--orchid-pink)',
    gradientTo: 'var(--hot-pink)',
  },
  dining: {
    label: 'Dining',
    value: 'dining',
    background: 'var(--aqua-pale)',
    color: 'var(--turquoise)',
    gradientFrom: 'var(--turquoise-light)',
    gradientTo: 'var(--turquoise)',
  },
  activities: {
    label: 'Activities',
    value: 'activities',
    background: 'var(--pink-blush)',
    color: 'var(--rose-pink)',
    gradientFrom: 'var(--orchid-pink)',
    gradientTo: 'var(--rose-pink)',
  },
  apartments: {
    label: 'Apartments',
    value: 'apartments',
    background: 'var(--lavender-soft)',
    color: 'var(--competitor-purple)',
    gradientFrom: 'var(--lavender-purple)',
    gradientTo: 'var(--competitor-purple)',
  },
  community: {
    label: 'Community First Impression',
    value: 'community',
    background: 'var(--background-gray-soft)',
    color: 'var(--neutral-category-text)',
    gradientFrom: 'var(--line-gray)',
    gradientTo: 'var(--line-gray-strong)',
  },
  communityexterior: {
    label: 'Community Exterior',
    value: 'community exterior',
    background: 'var(--background-gray-soft)',
    color: 'var(--neutral-category-text)',
    gradientFrom: 'var(--line-gray)',
    gradientTo: 'var(--line-gray-strong)',
  },
  comunityexterior: {
    label: 'Community Exterior',
    value: 'community exterior',
    background: 'var(--background-gray-soft)',
    color: 'var(--neutral-category-text)',
    gradientFrom: 'var(--line-gray)',
    gradientTo: 'var(--line-gray-strong)',
  },
  websiteexperience: {
    label: 'Website Experience',
    value: 'website-experience',
    background: 'var(--blue-mist)',
    color: 'var(--blue-vivid)',
    gradientFrom: 'var(--blue-sky)',
    gradientTo: 'var(--blue-vivid)',
  },
  reviews: {
    label: 'Reviews',
    value: 'reviews',
    background: 'var(--peach-pale)',
    color: 'var(--inactive-orange)',
    gradientFrom: 'var(--amber-light)',
    gradientTo: 'var(--inactive-orange)',
  },
  sales: {
    label: 'Sales',
    value: 'sales',
    background: 'var(--lavender-soft)',
    color: 'var(--competitor-purple)',
    gradientFrom: 'var(--lavender-purple)',
    gradientTo: 'var(--competitor-purple)',
  },
  operations: {
    label: 'Operations',
    value: 'operations',
    background: 'var(--background-gray-soft)',
    color: 'var(--neutral-category-text)',
    gradientFrom: 'var(--line-gray)',
    gradientTo: 'var(--line-gray-strong)',
  },
  other: {
    label: 'Other',
    value: 'other',
    background: 'var(--pink-blush)',
    color: 'var(--rose-pink)',
    gradientFrom: 'var(--orchid-pink)',
    gradientTo: 'var(--rose-pink)',
  },
  images: {
    label: 'Images',
    value: 'images',
    background: 'var(--lavender-soft)',
    color: 'var(--competitor-purple)',
    gradientFrom: 'var(--lavender-purple)',
    gradientTo: 'var(--competitor-purple)',
  },
  files: {
    label: 'Files',
    value: 'files',
    background: 'var(--background-gray-soft)',
    color: 'var(--neutral-category-text)',
    gradientFrom: 'var(--line-gray)',
    gradientTo: 'var(--line-gray-strong)',
  },
  emailmarketing: {
    label: 'Email Marketing',
    value: 'email_marketing',
    background: '#eff7ff',
    color: '#007aff',
    gradientFrom: '#007aff',
    gradientTo: '#007aff',
  },
};

export const AICI_COMMUNITY_FIELD = {
  COMMUNITY_NAME: 'community_name',
  SCORE: 'score',
  RANK: 'rank',
  STRONGEST_CATEGORIES: 'strongest_categories',
  WEAKEST_CATEGORIES: 'weakest_categories',
  TOP_COMPETITOR: 'top_competitor',
} as const;

export const AI_CI_CATEGORIES = [
  { key: 'online_reviews', label: 'Online Reviews' },
  { key: 'apartments', label: 'Apartments' },
  { key: 'dining', label: 'Dining' },
  { key: 'activities', label: 'Activities' },
  { key: 'amenities', label: 'Amenities' },
  { key: 'community_environment', label: 'Community First Impression' },
  { key: 'website_experience', label: 'Website Experience' },
] as const;

export const AI_CI_RANKING_TABS = [{ key: OVERALL_VIEW_KEY, label: 'Overall' }, ...AI_CI_CATEGORIES] as const;

export type AICIRankingTabKey = (typeof AI_CI_RANKING_TABS)[number]['key'];

export const AI_CI_CONFIG_NAME_PREFIX = 'AI Competitive Intelligence: ';

export const UNDEFINED_CATEGORY = {
  label: 'Undefined',
  background: 'var(--background-gray)',
  color: 'var(--neutral-category-text)',
  gradientFrom: 'var(--line-gray)',
  gradientTo: 'var(--line-gray-strong)',
};

export const REASONING_VARIANTS = {
  SUMMARY: 'summary',
  DETAILS: 'details',
} as const;

export type ReasoningVariantType = (typeof REASONING_VARIANTS)[keyof typeof REASONING_VARIANTS];

export const CATEGORY_VALUE_WIDGETS = {
  pricing: [WIDGET_TYPES.MARKET_RATE_GRAPH],
  community_fee: [WIDGET_TYPES.COMMUNITY_FEES_GRAPH],
  occupancy: [WIDGET_TYPES.OCCUPANCY_GRAPH],
  care_level: [WIDGET_TYPES.CARE_FEES_GRAPH],
  review_score: [WIDGET_TYPES.RATING_GRAPH],
  review_text: [WIDGET_TYPES.REVIEWS],
  amenities: [WIDGET_TYPES.AMENITIES],
};

export const RECOMMENDATION_OVERVIEW_FAILED = 'failed';

export const WEBSITE_VERIFICATION_FAILURES_QUERY_KEY = 'website-verification-failures';
